import { useState, useCallback } from 'react';
import { usePlatformAdminAuthStore } from '../../stores/platform-admin-auth.store';
import {
  platformOrganizationsService,
  Organization,
  OrganizationStats,
  CreateOrganizationDto,
  UpdateOrganizationDto,
  PauseOrganizationDto,
  DeleteOrganizationDto,
  GetOrganizationsParams,
  PaginatedOrganizations,
} from '../../services/platform-admin/platform-organizations.service';

export function usePlatformOrganizations() {
  const token = usePlatformAdminAuthStore((state) => state.token);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getStats = useCallback(async (): Promise<OrganizationStats | null> => {
    if (!token) {
      setError('Not authenticated');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const stats = await platformOrganizationsService.getStats(token);
      return stats;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch stats');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const getOrganizations = useCallback(
    async (params?: GetOrganizationsParams): Promise<PaginatedOrganizations | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await platformOrganizationsService.getOrganizations(token, params);
        return result;
      } catch (err: any) {
        setError(err.message || 'Failed to fetch organizations');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const getOrganizationById = useCallback(
    async (id: string): Promise<Organization | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const org = await platformOrganizationsService.getOrganizationById(token, id);
        return org;
      } catch (err: any) {
        setError(err.message || 'Failed to fetch organization');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const createOrganization = useCallback(
    async (data: CreateOrganizationDto): Promise<Organization | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const org = await platformOrganizationsService.createOrganization(token, data);
        return org;
      } catch (err: any) {
        setError(err.message || 'Failed to create organization');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const updateOrganization = useCallback(
    async (id: string, data: UpdateOrganizationDto): Promise<Organization | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const org = await platformOrganizationsService.updateOrganization(token, id, data);
        return org;
      } catch (err: any) {
        setError(err.message || 'Failed to update organization');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const pauseOrganization = useCallback(
    async (id: string, data: PauseOrganizationDto): Promise<Organization | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const org = await platformOrganizationsService.pauseOrganization(token, id, data);
        return org;
      } catch (err: any) {
        setError(err.message || 'Failed to pause organization');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const resumeOrganization = useCallback(
    async (id: string): Promise<Organization | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const org = await platformOrganizationsService.resumeOrganization(token, id);
        return org;
      } catch (err: any) {
        setError(err.message || 'Failed to resume organization');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const deleteOrganization = useCallback(
    async (id: string, data: DeleteOrganizationDto): Promise<boolean> => {
      if (!token) {
        setError('Not authenticated');
        return false;
      }

      setIsLoading(true);
      setError(null);

      try {
        await platformOrganizationsService.deleteOrganization(token, id, data);
        return true;
      } catch (err: any) {
        setError(err.message || 'Failed to delete organization');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const restoreOrganization = useCallback(
    async (id: string): Promise<Organization | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const org = await platformOrganizationsService.restoreOrganization(token, id);
        return org;
      } catch (err: any) {
        setError(err.message || 'Failed to restore organization');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  return {
    isLoading,
    error,
    getStats,
    getOrganizations,
    getOrganizationById,
    createOrganization,
    updateOrganization,
    pauseOrganization,
    resumeOrganization,
    deleteOrganization,
    restoreOrganization,
  };
}
